/**
 * An art gallery (Florence's Galleria dei Maestri; later the Louvre): a ticket at the desk, then the rooms. Standing
 * in front of a painting and taking it in is a little fun and a little culture each time, more the first time; see
 * them all and it's something you did. Which paintings you've seen is kept (by gallery), so the plaques can say so.
 */
import { cheaper } from './prices.ts';
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { EUR_NGN } from './travel.ts';

export const GALLERIES = {
  florence: { name: 'Galleria dei Maestri, Florence', short: 'the Galleria dei Maestri', ticket: 22 },
  louvre: { name: 'Musée du Louvre, Paris', short: 'the Louvre', ticket: 22 },
  lagos: { name: 'Adire House Gallery, Lekki', short: 'the Adire House Gallery', ticket: 2 },
} as const;
export type GalleryId = keyof typeof GALLERIES;
export const galleryTicket = (g: GalleryId) => cheaper(GALLERIES[g].ticket * EUR_NGN);

interface GalleryState { ticketDay?: Record<string, number>; seen?: Record<string, string[]> }
const of = (s: LifeState): GalleryState => ((s as LifeState & { art?: GalleryState }).art ??= {});

/** Today's ticket for this gallery (one a day lets you back in). */
export const hasTicket = (s: LifeState, g: GalleryId) => of(s).ticketDay?.[g] === Math.floor(s.minutes / 1440);

export function buyTicket(s: LifeState, g: GalleryId): { ok: boolean; line: string } {
  if (hasTicket(s, g)) return { ok: true, line: 'Your ticket from this morning still works.' };
  if (!spend(s, galleryTicket(g), `Ticket, ${GALLERIES[g].short}`)) return { ok: false, line: "You can't afford a ticket." };
  (of(s).ticketDay ??= {})[g] = Math.floor(s.minutes / 1440);
  return { ok: true, line: 'A paper ticket, torn at the edge. The guard waves you through.' };
}

/** You stand in front of a painting and take it in. */
export function viewArt(s: LifeState, g: GalleryId, id: string, total: number): { first: boolean; all: boolean; events: LifeEvent[] } {
  const seen = ((of(s).seen ??= {})[g] ??= []);
  const first = !seen.includes(id);
  if (first) seen.push(id);
  s.needs.fun = Math.min(100, s.needs.fun + (first ? 4 : 1));
  s.skills.creativity = Math.min(100, s.skills.creativity + (first ? 0.4 : 0.05));
  const all = first && seen.length >= total;
  if (all) s.history.push({ t: s.minutes, text: `Saw every painting in ${GALLERIES[g].short}.` });
  return { first, all, events: advance(s, first ? 4 : 1, { busy: true }) };
}
export const artSeen = (s: LifeState, g: GalleryId) => of(s).seen?.[g] ?? [];
